import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const FAQ_CATEGORIES = [
  { value: 'all',      label: 'All Questions' },
  { value: 'general',  label: 'General' },
  { value: 'pricing',  label: 'Pricing' },
  { value: 'process',  label: 'Our Process' },
  { value: 'services', label: 'Services' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'timeline', label: 'Timeline' },
];

// Default FAQs if none in DB
const DEFAULT_FAQS = [
  { _id: '1', category: 'general', question: 'What services does Wedoo Interiors offer?', answer: 'Wedoo Interiors provides complete interior design solutions including Modular Kitchens, Interior Design, Home Lighting, False Ceiling & Painting, Aluminium & Glass Works, and Complete Modular Works. We handle everything from initial concept to final handover.' },
  { _id: '2', category: 'pricing', question: 'How much does interior design cost in Tumakuru?', answer: 'Our pricing depends on the scope of work, materials chosen, and the size of your space. A typical 2BHK complete interior starts from ₹8-12 Lakhs. We offer a free consultation where we provide a detailed, transparent estimate with no hidden charges.' },
  { _id: '3', category: 'process', question: 'How long does a typical interior project take?', answer: 'A standard 2BHK modular interior project takes 45-60 days from design approval to handover. Larger projects or custom work may take 75-90 days. We provide a clear timeline at the start and stick to it — on-time delivery is our promise.' },
  { _id: '4', category: 'warranty', question: 'What warranty do you provide on your work?', answer: 'Wedoo Interiors provides a comprehensive 5-year warranty on all modular furniture and woodwork. Civil work like false ceilings and painting carries a 1-year warranty. Our warranty covers manufacturing defects, hardware failure, and finish issues.' },
  { _id: '5', category: 'process', question: 'Do you provide 3D design before starting work?', answer: 'Absolutely! Every project begins with a detailed 3D design presentation. You will see photorealistic renders of your space before a single nail is hammered. Revisions are included until you are 100% satisfied with the design.' },
  { _id: '6', category: 'pricing', question: 'What is the payment structure?', answer: 'Our payment is structured in three stages: 5% at design sign-off, 50% at production dispatch, and the remaining 45% at installation completion. We accept NEFT/RTGS, UPI, cheque, and card payments.' },
  { _id: '7', category: 'services', question: 'Do you do commercial interior projects?', answer: 'Yes, we handle commercial interiors including offices, retail showrooms, restaurants, and clinics. Our team is experienced in creating functional, brand-aligned commercial spaces on schedule and within budget.' },
  { _id: '8', category: 'general', question: 'Which areas do you serve?', answer: 'Our primary service area is Tumakuru and surrounding districts. We also serve clients in Bengaluru, Chitradurga, Hassan, and other parts of Karnataka. Contact us to confirm availability in your specific area.' },
  { _id: '9', category: 'timeline', question: 'Can you work on weekends?', answer: 'Our studio is open Monday to Saturday, 9 AM to 7 PM. Site work can be scheduled on Sundays if required, subject to prior agreement. We are flexible and work around your schedule to minimise disruption.' },
  { _id: '10', category: 'services', question: 'Do you offer modular kitchen installation only, without full interior design?', answer: 'Yes! You can engage us for individual services — modular kitchen alone, false ceiling only, or any single service. We are happy to customise the scope to match your exact requirements and budget.' },
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div style={{
      borderBottom: '1px solid var(--ink-5)',
      transition: 'all 0.3s',
    }}>
      <button onClick={onToggle} style={{
        width: '100%', textAlign: 'left', padding: '1.5rem 0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: '1.5rem', background: 'none', cursor: 'pointer',
      }}>
        <span style={{
          fontFamily: 'var(--font-serif)', fontSize: 'clamp(1rem,2vw,1.15rem)',
          fontWeight: 400, color: isOpen ? 'var(--gold)' : 'var(--cream)',
          lineHeight: 1.4, transition: 'color 0.3s', flex: 1,
        }}>
          {faq.question}
        </span>
        <span style={{
          width: '28px', height: '28px', flexShrink: 0,
          border: `1px solid ${isOpen ? 'var(--gold)' : 'var(--ink-5)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isOpen ? 'var(--gold)' : 'var(--muted)',
          fontSize: '1rem', transition: 'all 0.3s',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
        }}>+</span>
      </button>
      <div style={{
        maxHeight: isOpen ? '400px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.45s cubic-bezier(0.25,0.46,0.45,0.94)',
      }}>
        <p style={{
          color: 'var(--cream-2)', fontSize: '0.9rem', lineHeight: '1.9',
          paddingBottom: '1.5rem', paddingRight: '2rem',
        }}>
          {faq.answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [faqs, setFaqs]         = useState([]);
  const [category, setCategory] = useState('all');
  const [openId, setOpenId]     = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/faqs').then(r => setFaqs(r.data.length > 0 ? r.data : DEFAULT_FAQS))
      .catch(() => setFaqs(DEFAULT_FAQS))
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === 'all' ? faqs : faqs.filter(f => f.category === category);

  // Build FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": filtered.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ — Interior Design Questions | Wedoo Interiors Tumakuru</title>
        <meta name="description" content="Frequently asked questions about interior design, modular kitchens, pricing, warranty and our process at Wedoo Interiors, Tumakuru Karnataka." />
        <meta name="keywords" content="interior design FAQ, modular kitchen cost tumakuru, interior design process, wedoo interiors FAQ, interior design warranty" />
        <link rel="canonical" href="https://wedoointeriors.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Common Questions</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.2rem,5vw,3.5rem)', fontWeight:600, color: 'var(--cream)', marginBottom: '1rem' }}>
            Frequently Asked<br /><em style={{ color: 'var(--gold)' }}>Questions</em>
          </h1>
          <p style={{ color: 'var(--muted)', maxWidth: '520px', lineHeight: '1.8', fontSize: '0.9rem' }}>
            Everything you need to know about working with Wedoo Interiors — from pricing to process to warranty.
          </p>
        </div>
      </div>

      <section className="section-pad">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '4rem', alignItems: 'flex-start' }}>

            {/* Sidebar */}
            <div style={{ position: 'sticky', top: '7rem' }}>
              <p style={{ fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '1rem' }}>Filter by topic</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {FAQ_CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => { setCategory(c.value); setOpenId(null); }} style={{
                    textAlign: 'left', padding: '0.7rem 1rem', fontSize: '0.8rem',
                    fontWeight: '500', cursor: 'pointer', transition: 'all 0.3s',
                    background: category === c.value ? 'rgba(245,168,0,0.1)' : 'transparent',
                    color: category === c.value ? 'var(--gold)' : 'var(--muted)',
                    borderLeft: `2px solid ${category === c.value ? 'var(--gold)' : 'transparent'}`,
                  }}>
                    {c.label}
                    <span style={{ float: 'right', opacity: 0.5, fontSize: '0.7rem' }}>
                      {c.value === 'all' ? faqs.length : faqs.filter(f => f.category === c.value).length}
                    </span>
                  </button>
                ))}
              </div>

              {/* CTA */}
              <div style={{ marginTop: '2.5rem', padding: '1.5rem', background: 'var(--ink-2)', border: '1px solid var(--ink-4)', borderRadius:'var(--radius)' }}>
                <p style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.5rem' }}>Still have questions?</p>
                <p style={{ color: 'var(--muted)', fontSize: '0.8rem', lineHeight: '1.7', marginBottom: '1rem' }}>Our design experts are happy to help.</p>
                <Link to="/contact" className="btn btn-outline" style={{ padding: '0.65rem 1rem', fontSize: '0.65rem', width: '100%', justifyContent: 'center' }}>
                  Contact Us →
                </Link>
              </div>
            </div>

            {/* FAQ List */}
            <div>
              {loading ? (
                <div style={{ color: 'var(--muted)', padding: '3rem 0' }}>Loading questions...</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '3rem 0', color: 'var(--muted)' }}>No questions in this category yet.</div>
              ) : (
                <>
                  <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginBottom: '1.5rem' }}>
                    {filtered.length} question{filtered.length !== 1 ? 's' : ''}
                  </p>
                  {filtered.map((faq, i) => (
                    <FAQItem
                      key={faq._id || i}
                      faq={faq}
                      isOpen={openId === (faq._id || i)}
                      onToggle={() => setOpenId(openId === (faq._id || i) ? null : (faq._id || i))}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        <style>{`
          @media(max-width: 768px) {
            .container > div[style*="grid-template-columns: 220px"] {
              grid-template-columns: 1fr !important;
            }
            div[style*="position: sticky"] { position: static !important; }
          }
        `}</style>
      </section>

      {/* Bottom CTA */}
      <section style={{ padding: '5rem 0', background: 'var(--ink-2)', borderTop: '1px solid var(--ink-4)', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight:600, color: 'var(--cream)', marginBottom: '0.75rem' }}>
            Ready to Start Your <em style={{ color: 'var(--gold)' }}>Project?</em>
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: '460px', margin: '0 auto 2.5rem', lineHeight: '1.8' }}>
            Book a free, no-obligation consultation and get expert advice tailored to your space and budget.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/get-free-quote" className="btn btn-gold">Book Free Consultation</Link>
            <Link to="/reviews" className="btn btn-outline">Read Client Reviews</Link>
          </div>
        </div>
      </section>
    </>
  );
}
